import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import '../../styles/AdminPracticeHub.css';

// ─── Reusable Inline Components ───

function Breadcrumb({ items }) {
  return (
    <nav className="aph-breadcrumb">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="aph-bc-sep">›</span>}
          {i < items.length - 1 ? (
            <span className="aph-bc-link" onClick={item.onClick}>{item.label}</span>
          ) : (
            <span className="aph-bc-current">{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function StatusDot({ active, label }) {
  return (
    <span className={`aph-status ${active ? 'active' : 'inactive'}`}>
      <span className="aph-status-dot" />
      {label || (active ? 'Active' : 'Inactive')}
    </span>
  );
}

function RoleBadge({ role }) {
  const labels = { practice_admin: 'Practice Admin', clinical_admin: 'Clinical Admin', care_provider: 'Care Provider', super_admin: 'Super Admin' };
  const cls = { practice_admin: 'blue', clinical_admin: 'purple', care_provider: 'teal', super_admin: 'amber' };
  return <span className={`aph-role-badge ${cls[role] || 'blue'}`}>{labels[role] || role}</span>;
}

function Avatar({ initials, role }) {
  const cls = { practice_admin: 'blue', clinical_admin: 'purple', care_provider: 'teal', super_admin: 'amber' };
  return <span className={`aph-avatar ${cls[role] || 'blue'}`}>{initials}</span>;
}

// ─── Main Component ───

export default function AdminPracticeHub() {
  // Data
  const [groups, setGroups] = useState([]);
  const [practices, setPractices] = useState([]);
  const [users, setUsers] = useState([]);
  const [, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Navigation
  const [level, setLevel] = useState(1);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [activeTab, setActiveTab] = useState('practices');
  const [groupUsers, setGroupUsers] = useState([]);
  const [practiceAssignedUsers, setPracticeAssignedUsers] = useState([]); // users assigned to selected practice
  const [departments, setDepartments] = useState([]); // EHR departments for group
  const [, setDeptPractices] = useState([]); // practices returned from dept endpoint
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptError, setDeptError] = useState(null);
  const [assigningDept, setAssigningDept] = useState(null); // dept being assigned

  // Edit state
  const [editMode, setEditMode] = useState(null); // 'group' | 'practice' | 'user' | null
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // ─── Data Loading ───

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [gRes, pRes, uRes] = await Promise.all([
        api.get('/practice-groups'),
        api.get('/practices'),
        api.get('/users'),
      ]);
      setGroups(gRes.data || []);
      setPractices(pRes.data || []);
      setUsers(uRes.data || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadAssignmentsForUser = async (userId) => {
    try {
      const res = await api.get(`/assignments/user/${userId}`);
      return res.data || [];
    } catch { return []; }
  };

  // ─── Navigation ───

  const goLevel1 = () => {
    setLevel(1);
    setSelectedGroup(null);
    setSelectedPractice(null);
    setActiveTab('practices');
    closeForm();
  };

  const goLevel2 = async (group) => {
    setLevel(2);
    setSelectedGroup(group);
    setSelectedPractice(null);
    setActiveTab('practices');
    closeForm();
    // Fetch users for this group
    try {
      const res = await api.get(`/practice-groups/${group.id}/users`);
      setGroupUsers(res.data || []);
    } catch { setGroupUsers([]); }
  };

  const goLevel3 = async (practice) => {
    setLevel(3);
    setSelectedPractice(practice);
    setActiveTab('settings');
    closeForm();
    // Fetch users assigned to this practice
    try {
      const res = await api.get(`/assignments/practice/${practice.id}`);
      setPracticeAssignedUsers(res.data || []);
    } catch { setPracticeAssignedUsers([]); }
  };

  // ─── Form Handling ───

  const closeForm = () => {
    setEditMode(null);
    setEditingItem(null);
    setFormData({});
    setFormError('');
  };

  const openGroupForm = (group = null) => {
    setEditMode('group');
    setEditingItem(group);
    setFormData(group ? { name: group.name, description: group.description || '' } : { name: '', description: '' });
    setFormError('');
  };

  const openPracticeForm = (practice = null) => {
    setEditMode('practice');
    setEditingItem(practice);
    setFormData(practice ? {
      name: practice.name, address: practice.address || '', phone: practice.phone || '',
      email: practice.email || '', booking_url: practice.booking_url || '',
      revenue_per_visit: practice.revenue_per_visit || 150,
      timezone: practice.timezone || 'America/New_York', nh_practice_id: practice.nh_practice_id || '',
      practice_group_id: practice.practice_group_id || (selectedGroup?.id || ''),
      notifications_enabled: practice.notifications_enabled !== 0 && practice.notifications_enabled !== false,
    } : {
      name: '', address: '', phone: '', email: '', booking_url: '',
      revenue_per_visit: 150, timezone: 'America/New_York', nh_practice_id: '',
      practice_group_id: selectedGroup?.id || '',
      notifications_enabled: true,
    });
    setFormError('');
  };

  const openUserForm = async (user = null) => {
    setEditMode('user');
    setEditingItem(user);
    if (user) {
      const userAssigns = await loadAssignmentsForUser(user.id);
      setAssignments(userAssigns);
      setFormData({
        first_name: user.first_name || '', last_name: user.last_name || '',
        email: user.email || '', phone: user.phone || '',
        role: user.role || 'care_provider', password: '',
        assignmentMap: userAssigns.reduce((m, a) => ({ ...m, [a.practice_id]: a.role || 'practice_admin' }), {}),
      });
    } else {
      setAssignments([]);
      // Default: assign to the currently selected practice if in Level 3
      const defaultMap = selectedPractice ? { [selectedPractice.id]: 'practice_admin' } : {};
      setFormData({
        first_name: '', last_name: '', email: '', phone: '',
        role: 'practice_admin', password: '',
        assignmentMap: defaultMap,
      });
    }
    setFormError('');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const togglePracticeAssignment = (practiceId, checked) => {
    setFormData(prev => {
      const map = { ...prev.assignmentMap };
      if (checked) { map[practiceId] = 'practice_admin'; } else { delete map[practiceId]; }
      return { ...prev, assignmentMap: map };
    });
  };

  const setPracticeAssignmentRole = (practiceId, role) => {
    setFormData(prev => ({
      ...prev, assignmentMap: { ...prev.assignmentMap, [practiceId]: role }
    }));
  };

  // ─── Save Handlers ───

  const saveGroup = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/practice-groups/${editingItem.id}`, { name: formData.name, description: formData.description });
      } else {
        await api.post('/practice-groups', { name: formData.name, description: formData.description });
      }
      await loadAll();
      // Update selected group if we edited it
      if (editingItem && selectedGroup?.id === editingItem.id) {
        setSelectedGroup(prev => ({ ...prev, name: formData.name, description: formData.description }));
      }
      closeForm();
    } catch (err) { setFormError(err.message || 'Failed to save'); } finally { setSaving(false); }
  };

  const savePractice = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) { setFormError('Practice name is required'); return; }
    if (!formData.practice_group_id) { setFormError('Practice group is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        practice_group_id: parseInt(formData.practice_group_id),
        revenue_per_visit: parseFloat(formData.revenue_per_visit) || 150,
      };
      if (editingItem) {
        await api.put(`/practices/${editingItem.id}`, payload);
      } else {
        await api.post('/practices', payload);
      }
      await loadAll();
      // Update selected practice if we edited it
      if (editingItem && selectedPractice?.id === editingItem.id) {
        setSelectedPractice(prev => ({ ...prev, ...payload }));
      }
      closeForm();
    } catch (err) { setFormError(err.message || 'Failed to save'); } finally { setSaving(false); }
  };

  const saveUser = async (e) => {
    e.preventDefault();
    if (!formData.email?.trim()) { setFormError('Email is required'); return; }
    if (!formData.first_name?.trim()) { setFormError('First name is required'); return; }
    if (!editingItem && !formData.password) { setFormError('Password is required for new users'); return; }

    const assignmentEntries = Object.entries(formData.assignmentMap || {});
    if (formData.role !== 'super_admin' && assignmentEntries.length === 0) {
      setFormError('At least one practice assignment is required');
      return;
    }

    setSaving(true);
    try {
      const userPayload = {
        email: formData.email, firstName: formData.first_name, lastName: formData.last_name,
        phone: formData.phone, role: formData.role,
      };

      if (editingItem) {
        await api.put(`/users/${editingItem.id}`, userPayload);
        if (formData.role !== 'super_admin') {
          await api.put(`/assignments/user/${editingItem.id}`, {
            assignments: assignmentEntries.map(([pid, role]) => ({ practiceId: parseInt(pid), role }))
          });
        }
      } else {
        await api.post('/users', {
          ...userPayload, password: formData.password,
          assignments: assignmentEntries.map(([pid, role]) => ({ practiceId: parseInt(pid), role }))
        });
      }
      await loadAll();
      // Refresh context-specific user lists
      if (selectedGroup) {
        try { const res = await api.get(`/practice-groups/${selectedGroup.id}/users`); setGroupUsers(res.data || []); } catch {}
      }
      if (selectedPractice) {
        try { const res = await api.get(`/assignments/practice/${selectedPractice.id}`); setPracticeAssignedUsers(res.data || []); } catch {}
      }
      closeForm();
    } catch (err) { setFormError(err.message || 'Failed to save'); } finally { setSaving(false); }
  };

  // ─── Toggle Actions ───

  const toggleGroup = async (group) => {
    if (group.is_active) {
      if (!window.confirm(`Deactivate "${group.name}"?`)) return;
      await api.post(`/practice-groups/${group.id}/deactivate`);
    } else {
      await api.post(`/practice-groups/${group.id}/activate`);
    }
    await loadAll();
  };

  const togglePractice = async (practice) => {
    if (practice.enabled) {
      if (!window.confirm(`Disable "${practice.name}"? No messages will be sent.`)) return;
      await api.post(`/practices/${practice.id}/disable`);
    } else {
      await api.post(`/practices/${practice.id}/enable`);
    }
    await loadAll();
    if (selectedPractice?.id === practice.id) {
      setSelectedPractice(prev => ({ ...prev, enabled: !prev.enabled }));
    }
  };

  const toggleUser = async (user) => {
    if (user.is_active) {
      if (!window.confirm(`Deactivate "${user.first_name} ${user.last_name}"?`)) return;
      await api.post(`/users/${user.id}/deactivate`);
    } else {
      await api.post(`/users/${user.id}/activate`);
    }
    await loadAll();
    if (selectedGroup) {
      try { const res = await api.get(`/practice-groups/${selectedGroup.id}/users`); setGroupUsers(res.data || []); } catch {}
    }
  };

  const resetPassword = async (user) => {
    if (!window.confirm(`Reset password for ${user.email}?`)) return;
    try {
      await api.post(`/users/${user.id}/reset-password`);
      alert('Password reset email sent.');
    } catch (err) { alert(err.message || 'Failed to reset password'); }
  };

  // ─── Department Sync & Assignment ───

  const syncDepartments = async () => {
    if (!selectedGroup) return;
    setDeptLoading(true);
    setDeptError(null);
    try {
      const res = await api.get(`/practice-groups/${selectedGroup.id}/ehr/departments`);
      setDepartments(res.data?.departments || []);
      setDeptPractices(res.data?.practices || []);
      if (res.data?.errors?.length) {
        setDeptError(`Synced with ${res.data.errors.length} error(s): ${res.data.errors.map(e => e.error).join(', ')}`);
      }
    } catch (err) {
      setDeptError(err.message || 'Failed to sync departments from EHR');
      setDepartments([]);
    } finally { setDeptLoading(false); }
  };

  const assignDepartment = async (dept, targetPracticeId) => {
    if (!selectedGroup) return;
    setAssigningDept(dept.athena_department_id);
    try {
      await api.post(`/practice-groups/${selectedGroup.id}/ehr/departments/assign`, {
        assignments: [{
          practiceId: targetPracticeId,
          athenaDepartmentId: String(dept.athena_department_id),
          name: dept.name,
          address: dept.address || '',
          phone: dept.phone || '',
          fax: dept.fax || '',
          timezone: dept.timezone || '',
        }]
      });
      // Refresh departments
      await syncDepartments();
    } catch (err) {
      alert(err.message || 'Failed to assign department');
    } finally { setAssigningDept(null); }
  };

  const unassignDepartment = async (dept) => {
    if (!dept.localDepartmentId) {
      alert(`Cannot unassign: missing local department ID. Try syncing departments first.`);
      return;
    }
    if (!window.confirm(`Remove department "${dept.name}" from ${dept.practiceName || 'its practice'}? You can then reassign it to a different practice.`)) return;
    try {
      await api.delete(`/practice-groups/${selectedGroup.id}/ehr/departments/${dept.localDepartmentId}`);
      await syncDepartments();
    } catch (err) { alert(err.message || 'Failed to remove department'); }
  };

  const reassignDepartment = async (dept, newPracticeId) => {
    if (!selectedGroup) return;
    // Unassign from current practice, then assign to new one
    try {
      if (dept.localDepartmentId) {
        await api.delete(`/practice-groups/${selectedGroup.id}/ehr/departments/${dept.localDepartmentId}`);
      }
      await api.post(`/practice-groups/${selectedGroup.id}/ehr/departments/assign`, {
        assignments: [{
          practiceId: newPracticeId,
          athenaDepartmentId: String(dept.athena_department_id),
          name: dept.name,
          address: dept.address || '',
          phone: dept.phone || '',
          fax: dept.fax || '',
          timezone: dept.timezone || '',
        }]
      });
      await syncDepartments();
    } catch (err) { alert(err.message || 'Failed to reassign department'); }
  };

  // ─── Computed Data ───

  const groupPractices = selectedGroup ? practices.filter(p => p.practice_group_id === selectedGroup.id) : [];
  // groupUsers fetched in goLevel2, practiceAssignedUsers fetched in goLevel3

  const tzLabels = { 'America/New_York': 'Eastern', 'America/Chicago': 'Central', 'America/Denver': 'Mountain', 'America/Los_Angeles': 'Pacific' };
  const getInitials = (u) => `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase();

  // ─── Breadcrumbs ───

  const breadcrumbs = [{ label: 'Admin', onClick: goLevel1 }];
  if (level >= 1) breadcrumbs.push(level === 1 ? { label: 'Practice Groups' } : { label: 'Practice Groups', onClick: goLevel1 });
  if (level >= 2 && selectedGroup) breadcrumbs.push(level === 2 ? { label: selectedGroup.name } : { label: selectedGroup.name, onClick: () => goLevel2(selectedGroup) });
  if (level === 3 && selectedPractice) breadcrumbs.push({ label: selectedPractice.name });

  // ─── Loading / Error ───

  if (loading) return <div className="aph-loading">Loading...</div>;
  if (error) return <div className="aph-error">{error}</div>;

  // ─── LEVEL 1: Practice Groups Overview ───

  if (level === 1) {
    const totalPractices = practices.length;
    const totalUsers = users.filter(u => u.role !== 'super_admin').length;

    return (
      <div className="aph">
        <Breadcrumb items={breadcrumbs} />

        <div className="aph-page-header">
          <div>
            <h1 className="aph-title">Practice Groups</h1>
            <p className="aph-subtitle">Manage organizational groups and their associated practices</p>
          </div>
          <button className="aph-btn primary" onClick={() => openGroupForm()}>+ New Group</button>
        </div>

        {/* Stats */}
        <div className="aph-stats">
          <div className="aph-stat-card">
            <div className="aph-stat-icon blue">⊞</div>
            <div><div className="aph-stat-val">{groups.length}</div><div className="aph-stat-label">Practice Groups</div></div>
          </div>
          <div className="aph-stat-card">
            <div className="aph-stat-icon green">◫</div>
            <div><div className="aph-stat-val">{totalPractices}</div><div className="aph-stat-label">Total Practices</div></div>
          </div>
          <div className="aph-stat-card">
            <div className="aph-stat-icon purple">⊕</div>
            <div><div className="aph-stat-val">{totalUsers}</div><div className="aph-stat-label">Total Users</div></div>
          </div>
        </div>

        {/* Edit Form (inline) */}
        {editMode === 'group' && renderGroupForm()}

        {/* Group Cards */}
        <div className="aph-grid-3">
          {groups.map(g => {
            const gPractices = practices.filter(p => p.practice_group_id === g.id);
            const gUsers = g.userCount || 0;
            return (
              <div key={g.id} className="aph-card clickable" onClick={() => goLevel2(g)}>
                <div className="aph-card-top">
                  <div className="aph-card-icon blue">⊞</div>
                  <StatusDot active={g.is_active} />
                </div>
                <h3 className="aph-card-title">{g.name}</h3>
                <p className="aph-card-desc">{g.description || 'No description'}</p>
                <div className="aph-card-stats">
                  <span><strong>{gPractices.length}</strong> practices</span>
                  <span><strong>{gUsers}</strong> users</span>
                </div>
                <div className="aph-card-arrow">›</div>
              </div>
            );
          })}
          {groups.length === 0 && (
            <div className="aph-empty">No practice groups yet. Create one to get started.</div>
          )}
        </div>
      </div>
    );
  }

  // ─── LEVEL 2: Group Detail ───

  if (level === 2 && selectedGroup) {
    return (
      <div className="aph">
        <Breadcrumb items={breadcrumbs} />

        {/* Group Header */}
        <div className="aph-detail-header">
          <div className="aph-detail-left">
            <div className="aph-detail-icon blue">⊞</div>
            <div>
              <h1 className="aph-title">{selectedGroup.name}</h1>
              <p className="aph-subtitle">{selectedGroup.description || 'No description'}</p>
              <div className="aph-meta">
                <span>◫ {groupPractices.length} practices</span>
                <span>⊕ {groupUsers.length} users</span>
                <StatusDot active={selectedGroup.is_active} />
              </div>
            </div>
          </div>
          <div className="aph-detail-actions">
            <button className="aph-btn secondary" onClick={() => openGroupForm(selectedGroup)}>✎ Edit Group</button>
            <button className="aph-btn secondary" onClick={() => toggleGroup(selectedGroup)}>
              {selectedGroup.is_active ? '⊘ Deactivate' : '✓ Activate'}
            </button>
            <button className="aph-btn primary" onClick={() => openPracticeForm()}>+ Add Practice</button>
          </div>
        </div>

        {/* Edit Forms */}
        {editMode === 'group' && renderGroupForm()}
        {editMode === 'practice' && renderPracticeForm()}
        {editMode === 'user' && renderUserForm()}

        {/* Tabs */}
        <div className="aph-tabs">
          <button className={`aph-tab ${activeTab === 'practices' ? 'active' : ''}`} onClick={() => setActiveTab('practices')}>Practices</button>
          <button className={`aph-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>Users &amp; Roles</button>
          <button className={`aph-tab ${activeTab === 'departments' ? 'active' : ''}`} onClick={() => { setActiveTab('departments'); if (departments.length === 0 && !deptLoading) syncDepartments(); }}>EHR Departments</button>
        </div>

        {/* Practices Tab */}
        {activeTab === 'practices' && (
          <div className="aph-grid-3">
            {groupPractices.map(p => (
              <div key={p.id} className="aph-card clickable" onClick={() => goLevel3(p)}>
                <div className="aph-card-top">
                  <h3 className="aph-card-title" style={{ margin: 0 }}>{p.name}</h3>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {(p.notifications_enabled === 0 || p.notifications_enabled === false) && (
                      <span title="Master notification toggle is OFF — all notifications suppressed" aria-label="Notifications off">🔕</span>
                    )}
                    <span className={`aph-dot ${p.enabled ? 'green' : 'red'}`} title={p.enabled ? 'Enabled' : 'Disabled'} />
                  </span>
                </div>
                <div className="aph-card-details">
                  <div className="aph-detail-row">📞 {p.phone || 'No phone'}</div>
                  <div className="aph-detail-row">✉ {p.email || 'No email'}</div>
                  <div className="aph-detail-row">📍 {p.address || 'No address'}</div>
                </div>
                <div className="aph-card-footer">
                  <span className="aph-tz-badge">{tzLabels[p.timezone] || p.timezone || 'Eastern'}</span>
                  <span className="aph-card-arrow">›</span>
                </div>
              </div>
            ))}
            {groupPractices.length === 0 && (
              <div className="aph-empty">No practices in this group yet.</div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            <div style={{ marginBottom: 16 }}>
              <button className="aph-btn primary" onClick={() => openUserForm()}>+ Add User</button>
            </div>
            <div className="aph-table-wrap">
              <table className="aph-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupUsers.length === 0 ? (
                    <tr><td colSpan={5} className="aph-table-empty">No users assigned to this group.</td></tr>
                  ) : groupUsers.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div className="aph-user-cell">
                          <Avatar initials={getInitials(u)} role={u.role} />
                          <div>
                            <div className="aph-user-name">{u.first_name} {u.last_name}</div>
                            <div className="aph-user-email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td><RoleBadge role={u.role} /></td>
                      <td><StatusDot active={u.is_active} /></td>
                      <td className="aph-mono">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</td>
                      <td>
                        <div className="aph-action-btns">
                          <button className="aph-icon-btn" title="Edit" onClick={(e) => { e.stopPropagation(); openUserForm(u); }}>✎</button>
                          <button className="aph-icon-btn" title={u.is_active ? 'Deactivate' : 'Activate'} onClick={(e) => { e.stopPropagation(); toggleUser(u); }}>
                            {u.is_active ? '⊘' : '✓'}
                          </button>
                          <button className="aph-icon-btn" title="Reset Password" onClick={(e) => { e.stopPropagation(); resetPassword(u); }}>⟳</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Departments Tab */}
        {activeTab === 'departments' && (
          <div>
            <div className="aph-dept-toolbar">
              <div>
                <p className="aph-subtitle" style={{ margin: 0 }}>
                  Sync departments from EHR and assign them to practices. This controls which appointments each practice sees.
                </p>
              </div>
              <button className="aph-btn primary" onClick={syncDepartments} disabled={deptLoading}>
                {deptLoading ? '⟳ Syncing...' : '⟳ Sync from EHR'}
              </button>
            </div>

            {deptError && <div className="aph-form-error" style={{ marginBottom: 16 }}>{deptError}</div>}

            {deptLoading && <div className="aph-loading">Fetching departments from EHR...</div>}

            {!deptLoading && departments.length === 0 && (
              <div className="aph-empty-card">
                No departments found. Click "Sync from EHR" to fetch departments from the EHR system.
                {groupPractices.filter(p => p.nh_practice_id).length === 0 && (
                  <div style={{ marginTop: 8, color: '#dc2626', fontSize: 13 }}>
                    ⚠ No practices in this group have an NH Practice ID configured. Set it in practice settings first.
                  </div>
                )}
              </div>
            )}

            {!deptLoading && departments.length > 0 && (
              <div className="aph-table-wrap">
                <table className="aph-table">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th>Athena ID</th>
                      <th>Address</th>
                      <th>Phone</th>
                      <th>Timezone</th>
                      <th>Assigned To</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((dept, i) => {
                      return (
                        <tr key={`${dept.athena_department_id}-${dept.practice_id}-${i}`}>
                          <td><strong>{dept.name}</strong></td>
                          <td className="aph-mono">{dept.athena_department_id}</td>
                          <td style={{ fontSize: 12.5, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{dept.address || '—'}</td>
                          <td className="aph-mono">{dept.phone || '—'}</td>
                          <td>{dept.timezone ? <span className="aph-tz-badge">{tzLabels[dept.timezone] || dept.timezone}</span> : '—'}</td>
                          <td>
                            <select
                              className="aph-dept-select"
                              value={dept.isAssigned ? String(dept.practice_id) : ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (!val && dept.isAssigned) {
                                  unassignDepartment(dept);
                                } else if (val && !dept.isAssigned) {
                                  assignDepartment(dept, parseInt(val));
                                } else if (val && dept.isAssigned && parseInt(val) !== dept.practice_id) {
                                  reassignDepartment(dept, parseInt(val));
                                }
                              }}
                              disabled={assigningDept === dept.athena_department_id}
                            >
                              <option value="">— Not assigned —</option>
                              {groupPractices.filter(p => p.enabled).map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {dept.isAssigned && (
                              <button
                                className="aph-icon-btn"
                                title="Remove assignment"
                                onClick={() => unassignDepartment(dept)}
                              >✕</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── LEVEL 3: Practice Detail ───

  if (level === 3 && selectedPractice) {
    const p = selectedPractice;
    // Refresh from loaded practices in case data changed
    const freshPractice = practices.find(pr => pr.id === p.id) || p;

    return (
      <div className="aph">
        <Breadcrumb items={breadcrumbs} />

        {/* Practice Header */}
        <div className="aph-detail-header">
          <div className="aph-detail-left">
            <div className="aph-detail-icon green">◫</div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h1 className="aph-title">{freshPractice.name}</h1>
                <StatusDot active={freshPractice.enabled} label={freshPractice.enabled ? 'Enabled' : 'Disabled'} />
                {(freshPractice.notifications_enabled === 0 || freshPractice.notifications_enabled === false) && (
                  <span className="aph-role-badge red" title="Master notification toggle is OFF for this practice">🔕 Notifications OFF</span>
                )}
              </div>
              <p className="aph-subtitle">{freshPractice.address}</p>
              <div className="aph-meta">
                <span>📞 {freshPractice.phone || '—'}</span>
                <span>✉ {freshPractice.email || '—'}</span>
              </div>
            </div>
          </div>
          <div className="aph-detail-actions">
            <button className="aph-btn secondary" onClick={() => openPracticeForm(freshPractice)}>✎ Edit Practice</button>
            <button className="aph-btn secondary" onClick={() => togglePractice(freshPractice)}>
              {freshPractice.enabled ? '⊘ Disable' : '✓ Enable'}
            </button>
          </div>
        </div>

        {/* Edit Forms */}
        {editMode === 'practice' && renderPracticeForm()}
        {editMode === 'user' && renderUserForm()}

        {/* Tabs */}
        <div className="aph-tabs">
          <button className={`aph-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
          <button className={`aph-tab ${activeTab === 'access' ? 'active' : ''}`} onClick={() => setActiveTab('access')}>Users &amp; Access</button>
          <button className={`aph-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>Activity</button>
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="aph-settings-grid">
            <div className="aph-settings-card">
              <h3>General Information</h3>
              <p className="aph-card-subtitle">Core practice details</p>
              <SettingRow label="Name" value={freshPractice.name} />
              <SettingRow label="Address" value={freshPractice.address} />
              <SettingRow label="Phone" value={freshPractice.phone} mono />
              <SettingRow label="Email" value={freshPractice.email} />
              <SettingRow label="Booking URL" value={freshPractice.booking_url} link />
              <SettingRow label="Timezone" value={<span className="aph-tz-badge">{tzLabels[freshPractice.timezone] || freshPractice.timezone}</span>} />
              <SettingRow label="Revenue / Visit" value={`$${freshPractice.revenue_per_visit || 150}`} />
              <SettingRow label="NH Practice ID" value={freshPractice.nh_practice_id} mono />
            </div>
            <div className="aph-settings-card">
              <h3>Communication</h3>
              <p className="aph-card-subtitle">SMS, email, and voice config</p>
              {/* commons #232: master notification toggle state must be visible on
                  the Settings screen, not only inside the edit form (Satish, Aug 31). */}
              <SettingRow label="Notifications" value={
                freshPractice.notifications_enabled === 0 || freshPractice.notifications_enabled === false
                  ? <span className="aph-role-badge red" title="Master toggle OFF — every SMS/email/voice notification for this practice is suppressed">🔕 OFF — all notifications suppressed</span>
                  : <span className="aph-role-badge green" title="Master toggle ON — notification rules, consent and quiet hours apply as configured">🔔 Enabled</span>
              } />
              <SettingRow label="SMS Sender" value={freshPractice.sms_sender_phone} mono />
              <SettingRow label="Email Sender" value={freshPractice.email_sender_address} />
              <SettingRow label="Email Name" value={freshPractice.email_sender_name} />
              <SettingRow label="Voice Phone" value={freshPractice.voice_phone_number} mono />
              <SettingRow label="Voice Inbound" value={freshPractice.voice_inbound_phone} mono />
            </div>
            <div className="aph-settings-card full">
              <h3>Branding</h3>
              <p className="aph-card-subtitle">Visual identity and public assets</p>
              <SettingRow label="Brand Color" value={freshPractice.branding_color ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 16, height: 16, borderRadius: 4, background: freshPractice.branding_color, display: 'inline-block', border: '1px solid #ddd' }} />
                  <span className="aph-mono">{freshPractice.branding_color}</span>
                </span>
              ) : null} />
              <SettingRow label="Logo" value={freshPractice.logo_url} />
              <SettingRow label="Landing Page" value={freshPractice.landing_page_url} link />
            </div>
          </div>
        )}

        {/* Users & Access Tab */}
        {activeTab === 'access' && (
          <>
            <div style={{ marginBottom: 16 }}>
              <button className="aph-btn primary" onClick={() => openUserForm()}>+ Assign User</button>
            </div>
            <div className="aph-table-wrap">
              <table className="aph-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {practiceAssignedUsers.length === 0 ? (
                    <tr><td colSpan={5} className="aph-table-empty">No users assigned to this practice.</td></tr>
                  ) : practiceAssignedUsers.map(u => (
                    <tr key={u.user_id}>
                      <td>
                        <div className="aph-user-cell">
                          <Avatar initials={`${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase()} role={u.role} />
                          <div>
                            <div className="aph-user-name">{u.first_name} {u.last_name}</div>
                            <div className="aph-user-email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td><RoleBadge role={u.role} /></td>
                      <td><StatusDot active={u.is_active} /></td>
                      <td className="aph-mono">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</td>
                      <td>
                        <div className="aph-action-btns">
                          <button className="aph-icon-btn" title="Remove from practice" onClick={async () => {
                            if (!window.confirm(`Remove ${u.first_name} ${u.last_name} from this practice?`)) return;
                            try {
                              await api.delete(`/assignments/${u.user_id}/${freshPractice.id}`);
                              const res = await api.get(`/assignments/practice/${freshPractice.id}`);
                              setPracticeAssignedUsers(res.data || []);
                            } catch (err) { alert(err.message || 'Failed to remove'); }
                          }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <ActivityFeed practiceId={freshPractice.id} />
        )}
      </div>
    );
  }

  return null;

  // ─── Form Renderers ───

  function renderGroupForm() {
    return (
      <div className="aph-form-panel">
        <h3>{editingItem ? 'Edit Practice Group' : 'New Practice Group'}</h3>
        {formError && <div className="aph-form-error">{formError}</div>}
        <form onSubmit={saveGroup}>
          <div className="aph-form-row">
            <label>Name *</label>
            <input type="text" value={formData.name || ''} onChange={e => handleChange('name', e.target.value)} disabled={saving} placeholder="Group name" />
          </div>
          <div className="aph-form-row">
            <label>Description</label>
            <textarea value={formData.description || ''} onChange={e => handleChange('description', e.target.value)} disabled={saving} rows={3} placeholder="Optional description" />
          </div>
          <div className="aph-form-actions">
            <button type="submit" className="aph-btn primary" disabled={saving}>{saving ? 'Saving...' : editingItem ? 'Update Group' : 'Create Group'}</button>
            <button type="button" className="aph-btn secondary" onClick={closeForm} disabled={saving}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  function renderPracticeForm() {
    return (
      <div className="aph-form-panel">
        <h3>{editingItem ? 'Edit Practice' : 'New Practice'}</h3>
        {formError && <div className="aph-form-error">{formError}</div>}
        <form onSubmit={savePractice}>
          <div className="aph-form-grid">
            <div className="aph-form-row">
              <label>Practice Name *</label>
              <input type="text" value={formData.name || ''} onChange={e => handleChange('name', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Practice Group *</label>
              <select value={formData.practice_group_id || ''} onChange={e => handleChange('practice_group_id', e.target.value)} disabled={saving}>
                <option value="">Select group</option>
                {groups.filter(g => g.is_active).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="aph-form-row">
              <label>Address</label>
              <input type="text" value={formData.address || ''} onChange={e => handleChange('address', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Phone</label>
              <input type="tel" value={formData.phone || ''} onChange={e => handleChange('phone', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Email</label>
              <input type="email" value={formData.email || ''} onChange={e => handleChange('email', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Booking URL</label>
              <input type="url" value={formData.booking_url || ''} onChange={e => handleChange('booking_url', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Revenue / Visit ($)</label>
              <input type="number" value={formData.revenue_per_visit || ''} onChange={e => handleChange('revenue_per_visit', e.target.value)} disabled={saving} min="0" step="0.01" />
            </div>
            <div className="aph-form-row">
              <label>Timezone</label>
              <select value={formData.timezone || 'America/New_York'} onChange={e => handleChange('timezone', e.target.value)} disabled={saving}>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>
            <div className="aph-form-row">
              <label>NH Practice ID</label>
              <input type="text" value={formData.nh_practice_id || ''} onChange={e => handleChange('nh_practice_id', e.target.value)} disabled={saving} placeholder="Internal ID" />
            </div>
            <div className="aph-form-row" style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.notifications_enabled !== false}
                  onChange={e => handleChange('notifications_enabled', e.target.checked)}
                  disabled={saving}
                />
                <span>Notifications enabled (master toggle)</span>
              </label>
              <small style={{ color: '#6b7280', marginLeft: 24 }}>
                When OFF, all appointment reminders, confirmations, cancellations, and campaign messages are suppressed for this practice.
              </small>
            </div>
          </div>
          <div className="aph-form-actions">
            <button type="submit" className="aph-btn primary" disabled={saving}>{saving ? 'Saving...' : editingItem ? 'Update Practice' : 'Create Practice'}</button>
            <button type="button" className="aph-btn secondary" onClick={closeForm} disabled={saving}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  function renderUserForm() {
    return (
      <div className="aph-form-panel">
        <h3>{editingItem ? `Edit User: ${editingItem.first_name} ${editingItem.last_name}` : 'New User'}</h3>
        {formError && <div className="aph-form-error">{formError}</div>}
        <form onSubmit={saveUser}>
          <div className="aph-form-grid">
            <div className="aph-form-row">
              <label>First Name *</label>
              <input type="text" value={formData.first_name || ''} onChange={e => handleChange('first_name', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Last Name</label>
              <input type="text" value={formData.last_name || ''} onChange={e => handleChange('last_name', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Email *</label>
              <input type="email" value={formData.email || ''} onChange={e => handleChange('email', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Phone</label>
              <input type="tel" value={formData.phone || ''} onChange={e => handleChange('phone', e.target.value)} disabled={saving} />
            </div>
            <div className="aph-form-row">
              <label>Role *</label>
              <select value={formData.role || ''} onChange={e => handleChange('role', e.target.value)} disabled={saving}>
                <option value="practice_admin">Practice Admin</option>
                <option value="clinical_admin">Clinical Admin</option>
                <option value="care_provider">Care Provider</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            {!editingItem && (
              <div className="aph-form-row">
                <label>Password *</label>
                <input type="password" value={formData.password || ''} onChange={e => handleChange('password', e.target.value)} disabled={saving} />
              </div>
            )}
          </div>

          {formData.role !== 'super_admin' && (
            <div className="aph-form-assignments">
              <label>Practice Assignments</label>
              <div className="aph-assign-list">
                {groups.filter(g => g.is_active).map(g => {
                  const gPractices = practices.filter(p => p.practice_group_id === g.id && p.enabled);
                  if (gPractices.length === 0) return null;
                  return (
                    <div key={g.id}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.8, padding: '8px 0 4px' }}>{g.name}</div>
                      {gPractices.map(p => {
                        const isAssigned = !!formData.assignmentMap?.[p.id];
                        return (
                          <div key={p.id} className="aph-assign-row">
                            <label className="aph-checkbox-label">
                              <input type="checkbox" checked={isAssigned} onChange={e => togglePracticeAssignment(p.id, e.target.checked)} />
                              {p.name}
                            </label>
                            {isAssigned && (
                              <select value={formData.assignmentMap[p.id]} onChange={e => setPracticeAssignmentRole(p.id, e.target.value)}>
                                <option value="practice_admin">Practice Admin</option>
                                <option value="clinical_admin">Clinical Admin</option>
                                <option value="care_provider">Care Provider</option>
                              </select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="aph-form-actions">
            <button type="submit" className="aph-btn primary" disabled={saving}>{saving ? 'Saving...' : editingItem ? 'Update User' : 'Create User'}</button>
            <button type="button" className="aph-btn secondary" onClick={closeForm} disabled={saving}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }
}

// ─── Setting Row (for Level 3) ───

function SettingRow({ label, value, mono, link }) {
  const display = value || <span className="aph-muted">Not set</span>;
  return (
    <div className="aph-setting-row">
      <span className="aph-setting-label">{label}</span>
      <span className={`aph-setting-value ${mono ? 'aph-mono' : ''}`}>
        {link && value ? <a href={value} target="_blank" rel="noopener noreferrer">{value.replace('https://', '')}</a> : display}
      </span>
    </div>
  );
}

// ─── Activity Feed (for Level 3) ───

function ActivityFeed({ practiceId }) {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/admin/audit-log?limit=20&entity_type=practice&entity_id=${practiceId}`);
        setLogs(res.data || []);
      } catch { setLogs([]); }
      setLoadingLogs(false);
    })();
  }, [practiceId]);

  if (loadingLogs) return <div className="aph-loading">Loading activity...</div>;

  if (logs.length === 0) {
    return <div className="aph-empty-card">No recent activity for this practice.</div>;
  }

  return (
    <div className="aph-activity-list">
      {logs.map((log, i) => (
        <div key={i} className="aph-activity-item">
          <span className={`aph-activity-dot ${log.action === 'create' ? 'green' : log.action === 'update' ? 'blue' : 'amber'}`} />
          <div>
            <div className="aph-activity-text">
              <strong>{log.user_email || 'System'}</strong> {log.action} {log.entity_type}
              {log.entity_id ? ` #${log.entity_id}` : ''}
            </div>
            <div className="aph-activity-time">{new Date(log.created_at).toLocaleString()}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
